import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useCurrency } from "../../../context/CurrencyContext";
import { usePricingData, PricingTier } from "../../../hooks/usePricingData";
import { useSaleOpen } from "../../../hooks/useSaleOpen";
import { useVerticalPricing } from "../../../hooks/useVerticalPricing";
import { resolveTierFeatures, canonicalVerticalSlug } from "../../../utils/verticalTierLimits";

// Images
import bgImg from "../../../images/bg/pricing-bg.png";
import bgItemImg from "../../../images/bg/pricing-bg01.png";
import iconLeft from "../../../images/icon/sub-left-icon.png";
import iconRight from "../../../images/icon/sub-right-icon.png";
import moneyGif from "../../../images/icon/money-icegif-22-unscreen.gif";
import icon1 from "../../../images/icon/pricing-icon01.svg";
import icon2 from "../../../images/icon/pricing-icon02.svg";

// SVG Check Icon
const CheckIcon: React.FC = () => (
  <svg width="21" height="21" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path opacity="0.1" d="M21 10.5C21 11.396 19.8993 12.1345 19.6787 12.9605C19.4513 13.8145 20.027 15.0045 19.5947 15.7517C19.1555 16.5112 17.8342 16.6022 17.2183 17.2183C16.6022 17.8342 16.5112 19.1555 15.7517 19.5947C15.0045 20.027 13.8145 19.4513 12.9605 19.6787C12.1345 19.8993 11.396 21 10.5 21C9.604 21 8.8655 19.8993 8.0395 19.6787C7.1855 19.4513 5.9955 20.027 5.24825 19.5947C4.48875 19.1555 4.39775 17.8342 3.78175 17.2183C3.16575 16.6022 1.8445 16.5112 1.40525 15.7517C0.973 15.0045 1.54875 13.8145 1.32125 12.9605C1.10075 12.1345 0 11.396 0 10.5C0 9.604 1.10075 8.8655 1.32125 8.0395C1.54875 7.1855 0.973 5.9955 1.40525 5.24825C1.8445 4.48875 3.16575 4.39775 3.78175 3.78175C4.39775 3.16575 4.48875 1.8445 5.24825 1.40525C5.9955 0.973 7.1855 1.54875 8.0395 1.32125C8.8655 1.10075 9.604 0 10.5 0C11.396 0 12.1345 1.10075 12.9605 1.32125C13.8145 1.54875 15.0045 0.973 15.7517 1.40525C16.5112 1.8445 16.6022 3.16575 17.2183 3.78175C17.8342 4.39775 19.1555 4.48875 19.5947 5.24825C20.027 5.9955 19.4513 7.1855 19.6787 8.0395C19.8993 8.8655 21 9.604 21 10.5Z" fill="var(--color-primary)" />
    <path d="M13.5336 7.37076L9.53661 11.3678L7.46461 9.29751C7.01486 8.84776 6.28511 8.84776 5.83536 9.29751C5.38561 9.74726 5.38561 10.477 5.83536 10.9268L8.74211 13.8335C9.17961 14.271 9.89011 14.271 10.3276 13.8335L15.1611 9.00001C15.6109 8.55026 15.6109 7.82051 15.1611 7.37076C14.7114 6.92101 13.9834 6.92101 13.5336 7.37076Z" fill="var(--color-primary)" />
  </svg>
);

// SVG Cross Icon
const CrossIcon: React.FC = () => (
  <svg width="21" height="21" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path opacity="0.1" d="M21 10.5C21 16.299 16.299 21 10.5 21C4.701 21 0 16.299 0 10.5C0 4.701 4.701 0 10.5 0C16.299 0 21 4.701 21 10.5Z" fill="#666" />
    <path d="M13.5 7.5L7.5 13.5M7.5 7.5L13.5 13.5" stroke="#666" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

// Helper function to render comparison values
const renderComparisonValue = (value: string | boolean) => {
  if (value === true) return <CheckIcon />;
  if (value === false) return <CrossIcon />;
  return <span className="opacity-75">{value}</span>;
};

export interface PricingSectionProps {
  hideBg?: boolean;
  hideComparison?: boolean;
  hideAddons?: boolean;
  hideFaqHelp?: boolean;
  sectionId?: string;
  className?: string;
  verticalSlug?: string;
  customSubtitle?: string;
  customTitle?: string;
  customDesc?: string;
}

const PricingSection: React.FC<PricingSectionProps> = ({
  hideBg = false,
  hideComparison = false,
  hideAddons = false,
  hideFaqHelp = false,
  sectionId = "pricing",
  className,
  verticalSlug,
  customSubtitle,
  customTitle,
  customDesc,
}) => {
  const { t } = useTranslation();
  const { formatPrice } = useCurrency();
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'quarterly' | 'six_month' | 'yearly'>('monthly');

  // Get pricing data from hook (first fetches from Mothership, falls back to hardcoded if no data)
  const {
    pricingTiers: PRICING_TIERS,
    specialtyAddons: SPECIALTY_ADDONS,
    featureComparison: FEATURE_COMPARISON,
    comparisonHeaders,
    loading: pricingLoading,
    isFromAPI,
  } = usePricingData();

  // Fetch verticals from Mothership to get live vertical_limits when available
  const { verticals } = useVerticalPricing();

  const activeLiveVerticalLimits = React.useMemo(() => {
    if (!verticalSlug || !verticals || verticals.length === 0) return null;
    const canonical = canonicalVerticalSlug(verticalSlug);
    const found = verticals.find((v) => v.id === canonical || v.id === verticalSlug);
    return found?.vertical_limits ?? null;
  }, [verticalSlug, verticals]);

  // Is the storefront open? Same answer the signup form and checkout use.
  // Before this, the paid tiers carried a hardcoded `comingSoon: false` and
  // advertised a live "Get Started" CTA into a signup form that refused to
  // sell them — this page promised what the next page took away.
  const { saleOpen, loading: saleLoading } = useSaleOpen();

  // Only self-serve paid tiers depend on the switch. Explorer is free and
  // Titan is sales-led (its CTA goes to /contact), so neither is affected.
  const isPaidSelfServe = (tier: PricingTier) =>
    (tier.monthlyPrice ?? 0) > 0 && tier.ctaLink.includes('/signup');

  // `comingSoon` stays the flag for things genuinely not built yet (the addon
  // packs). Sellability is a separate, server-owned question.
  //
  // Deliberately unlocked while the answer is in flight: failing closed here
  // would flash "Coming Soon" across the pricing table on every page load
  // once we are live. Nothing is at risk in that window — the CTA only links
  // to /signup, which fails closed itself, and the server refuses the order.
  const isTierLocked = (tier: PricingTier) =>
    tier.comingSoon === true || (isPaidSelfServe(tier) && !saleLoading && !saleOpen);

  // Get discounted price (what customer pays)
  const getPrice = (tier: PricingTier) => {
    // Free plan
    if (tier.monthlyPrice === 0) return t('pricing.freeForever') || 'Free Forever';
    // Titan - show 5-year price (10-year shown separately below)
    if (tier.fiveYearPrice && !tier.monthlyPrice) {
      return formatPrice(tier.fiveYearPrice);
    }
    // Regular plans - show based on billing period toggle
    switch (billingPeriod) {
      case 'monthly':
        if (tier.monthlyPrice && tier.monthlyPrice > 0) return formatPrice(tier.monthlyPrice);
        break;
      case 'quarterly':
        if (tier.quarterlyPrice && tier.quarterlyPrice > 0) return formatPrice(tier.quarterlyPrice);
        break;
      case 'six_month':
        if (tier.sixMonthPrice && tier.sixMonthPrice > 0) return formatPrice(tier.sixMonthPrice);
        break;
      case 'yearly':
        if (tier.yearlyPrice && tier.yearlyPrice > 0) return formatPrice(tier.yearlyPrice);
        break;
    }
    return t('pricing.contactUs') || 'Contact Us';
  };

  // Get original price (crossed out)
  const getOriginalPrice = (tier: PricingTier) => {
    // Free plan - no original price
    if (tier.monthlyPrice === 0 || tier.discountPercent === 0) return null;
    // Titan - show original 5-year price crossed out
    if (tier.fiveYearPrice && !tier.monthlyPrice && tier.originalFiveYearPrice) {
      return formatPrice(tier.originalFiveYearPrice);
    }
    // Regular plans
    switch (billingPeriod) {
      case 'monthly':
        if (tier.originalMonthlyPrice && tier.originalMonthlyPrice > 0) return formatPrice(tier.originalMonthlyPrice);
        break;
      case 'quarterly':
        if (tier.originalQuarterlyPrice && tier.originalQuarterlyPrice > 0) return formatPrice(tier.originalQuarterlyPrice);
        break;
      case 'six_month':
        if (tier.originalSixMonthPrice && tier.originalSixMonthPrice > 0) return formatPrice(tier.originalSixMonthPrice);
        break;
      case 'yearly':
        if (tier.originalYearlyPrice && tier.originalYearlyPrice > 0) return formatPrice(tier.originalYearlyPrice);
        break;
    }
    return null;
  };

  const getPeriod = (tier: PricingTier) => {
    // Free plan
    if (tier.monthlyPrice === 0) return '';
    // Titan - show 5-year period
    if (tier.fiveYearPrice && !tier.monthlyPrice) return '/ 5 Years';
    // Regular plans
    switch (billingPeriod) {
      case 'monthly':
        if (tier.monthlyPrice && tier.monthlyPrice > 0) return '/ Month';
        break;
      case 'quarterly':
        if (tier.quarterlyPrice && tier.quarterlyPrice > 0) return '/ 3 Months';
        break;
      case 'six_month':
        if (tier.sixMonthPrice && tier.sixMonthPrice > 0) return '/ 6 Months';
        break;
      case 'yearly':
        if (tier.yearlyPrice && tier.yearlyPrice > 0) return '/ Year';
        break;
    }
    return '';
  };

  return (
    <>
      <style>{`
        @keyframes pricingPulse {
          0% { opacity: 0.35; }
          50% { opacity: 0.75; }
          100% { opacity: 0.35; }
        }
        .pricing-comparison-card {
          background-color: #0b1329;
          border: 1px solid rgba(255, 255, 255, 0.12);
          border-radius: 16px;
          overflow: hidden;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.45);
        }
        .pricing-comparison-table {
          width: 100%;
          border-collapse: collapse;
          background: #0b1329;
          margin-bottom: 0;
        }
        .pricing-comparison-table thead th {
          background: #121c38;
          border-bottom: 1px solid rgba(255, 255, 255, 0.12);
          padding: 18px 20px;
          font-size: 14px;
          font-weight: 700;
          color: #fff;
          text-align: center;
          letter-spacing: 0.5px;
        }
        .pricing-comparison-table thead th.feature-name-col {
          text-align: left;
          color: #fff;
          width: 32%;
          padding-left: 24px;
        }
        .pricing-comparison-table thead th.table-highlight {
          color: var(--color-primary);
          background: #18264a;
        }
        .pricing-comparison-table tr.category-header td {
          background: rgba(0, 255, 151, 0.08);
          border-top: 1px solid rgba(255, 255, 255, 0.1);
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
          color: #fff;
          font-size: 13px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 1px;
          padding: 14px 24px;
        }
        .pricing-comparison-table tbody tr {
          border-bottom: 1px solid rgba(255, 255, 255, 0.06);
          transition: background 0.2s ease;
        }
        .pricing-comparison-table tbody tr:hover {
          background: rgba(255, 255, 255, 0.03);
        }
        .pricing-comparison-table tbody tr:last-child {
          border-bottom: none;
        }
        .pricing-comparison-table tbody td {
          padding: 14px 20px;
          font-size: 14px;
          text-align: center;
          vertical-align: middle;
          color: #e2e8f0;
        }
        .pricing-comparison-table tbody td.feature-name-cell {
          font-weight: 500;
          color: #fff;
          text-align: left;
          padding-left: 24px;
        }
        .pricing-comparison-table tbody td.table-highlight {
          background: rgba(0, 255, 151, 0.04);
        }
        .pricing-comparison-footer {
          padding: 20px 24px;
          background: #0f1833;
          border-top: 1px solid rgba(255, 255, 255, 0.1);
          text-align: center;
          border-bottom-left-radius: 15px;
          border-bottom-right-radius: 15px;
        }
        .pricing-comparison-footer p {
          font-size: 13px;
          opacity: 0.85;
          color: #cbd5e1;
          margin: 0;
          line-height: 1.6;
          max-width: 760px;
          margin-left: auto;
          margin-right: auto;
        }
      `}</style>
      {/* ========== PRICING PLANS SECTION ========== */}
      <section
        id={sectionId}
        className={className ? className : `pricing pt-145 pb-100 ${hideBg ? "" : "bg_img"}`}
        style={hideBg ? undefined : { backgroundImage: `url(${bgImg})` }}
      >
        <div className="container">
          {/* Section Title */}
          <div className="sec-title sec-title--two pricing-sec-title text-center mb-30">
            <span className="sub-title wow fadeInUp" data-wow-duration="600ms">
              <img src={iconLeft} alt="icon" /> {customSubtitle || t('pricing.subtitle') || 'AXOIX Plans'} <img src={iconRight} alt="icon" />
            </span>
            <h2 className="title wow fadeInUp" data-wow-delay="150ms" data-wow-duration="600ms">
              {customTitle || t('pricing.title') || 'Choose your command level'} <img src={moneyGif} alt="shape" />
            </h2>
            {customDesc && (
              <p className="mt-20 opacity-75 wow fadeInUp" data-wow-delay="200ms" style={{ maxWidth: 720, margin: "15px auto 0" }}>
                {customDesc}
              </p>
            )}
          </div>

          {/* Billing Toggle - 4 Options */}
          <div className="text-center mb-50">
            <div className="pricing-item d-inline-block xb-price-chagne-btn">
              <div className="d-flex align-items-center justify-content-center gap-2 flex-wrap">
                <button
                  className={`btn btn-sm px-3 py-2 ${billingPeriod === 'monthly' ? 'btn-primary' : 'btn-outline-light opacity-75'}`}
                  onClick={() => setBillingPeriod('monthly')}
                  style={{ borderRadius: '20px', minWidth: '100px' }}
                >
                  {t('pricing.monthly') || 'Monthly'}
                </button>
                <button
                  className={`btn btn-sm px-3 py-2 ${billingPeriod === 'quarterly' ? 'btn-primary' : 'btn-outline-light opacity-75'}`}
                  onClick={() => setBillingPeriod('quarterly')}
                  style={{ borderRadius: '20px', minWidth: '100px' }}
                >
                  {t('pricing.quarterly') || '3 Months'}
                  <span className="badge bg-success ms-1" style={{ fontSize: '10px' }}>5% OFF</span>
                </button>
                <button
                  className={`btn btn-sm px-3 py-2 ${billingPeriod === 'six_month' ? 'btn-primary' : 'btn-outline-light opacity-75'}`}
                  onClick={() => setBillingPeriod('six_month')}
                  style={{ borderRadius: '20px', minWidth: '100px' }}
                >
                  {t('pricing.sixMonth') || '6 Months'}
                  <span className="badge bg-success ms-1" style={{ fontSize: '10px' }}>10% OFF</span>
                </button>
                <button
                  className={`btn btn-sm px-3 py-2 ${billingPeriod === 'yearly' ? 'btn-primary' : 'btn-outline-light opacity-75'}`}
                  onClick={() => setBillingPeriod('yearly')}
                  style={{ borderRadius: '20px', minWidth: '100px' }}
                >
                  {t('pricing.yearly') || 'Yearly'}
                  <span className="badge bg-warning text-dark ms-1" style={{ fontSize: '10px' }}>BEST VALUE</span>
                </button>
              </div>
            </div>
          </div>

          {/* Pricing Cards - 4 Plans */}
          {pricingLoading ? (
            <div className="row mt-none-30">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="col-lg-6 col-md-6 mt-30">
                  <div
                    className="pricing-item xb-border bg_img"
                    style={{
                      backgroundImage: `url(${bgItemImg})`,
                      padding: "40px 30px",
                      borderRadius: "16px",
                    }}
                  >
                    <div
                      style={{
                        width: "48px",
                        height: "48px",
                        borderRadius: "12px",
                        background: "rgba(255, 255, 255, 0.08)",
                        marginBottom: "20px",
                        animation: "pricingPulse 1.5s infinite ease-in-out",
                      }}
                    />
                    <div
                      style={{
                        width: "130px",
                        height: "18px",
                        borderRadius: "4px",
                        background: "rgba(255, 255, 255, 0.08)",
                        marginBottom: "12px",
                        animation: "pricingPulse 1.5s infinite ease-in-out",
                      }}
                    />
                    <div
                      style={{
                        width: "180px",
                        height: "36px",
                        borderRadius: "6px",
                        background: "rgba(0, 255, 151, 0.15)",
                        marginBottom: "18px",
                        animation: "pricingPulse 1.5s infinite ease-in-out",
                      }}
                    />
                    <div
                      style={{
                        width: "100%",
                        height: "16px",
                        borderRadius: "4px",
                        background: "rgba(255, 255, 255, 0.05)",
                        marginBottom: "25px",
                        animation: "pricingPulse 1.5s infinite ease-in-out",
                      }}
                    />
                    <div
                      style={{
                        width: "100%",
                        height: "48px",
                        borderRadius: "30px",
                        background: "rgba(255, 255, 255, 0.08)",
                        marginBottom: "30px",
                        animation: "pricingPulse 1.5s infinite ease-in-out",
                      }}
                    />
                    {[1, 2, 3, 4, 5].map((bullet) => (
                      <div
                        key={bullet}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                          marginBottom: "14px",
                        }}
                      >
                        <div
                          style={{
                            width: "18px",
                            height: "18px",
                            borderRadius: "50%",
                            background: "rgba(0, 255, 151, 0.15)",
                            flexShrink: 0,
                          }}
                        />
                        <div
                          style={{
                            width: `${50 + bullet * 8}%`,
                            height: "12px",
                            borderRadius: "3px",
                            background: "rgba(255, 255, 255, 0.06)",
                            animation: "pricingPulse 1.5s infinite ease-in-out",
                          }}
                        />
                      </div>
                    ))}
                    <div
                      className="text-center mt-20 pt-10"
                      style={{
                        borderTop: "1px solid rgba(255, 255, 255, 0.06)",
                        fontSize: "12px",
                        color: "rgba(0, 255, 151, 0.8)",
                        letterSpacing: "0.06em",
                      }}
                    >
                      <i className="fas fa-circle-notch fa-spin me-2"></i>
                      Fetching Mothership Pricing...
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="row mt-none-30">
              {PRICING_TIERS.map((tier, index) => (
              <div key={tier.id} className="col-lg-6 col-md-6 mt-30">
                <div
                  className={`pricing-item xb-border bg_img wow fadeInUp ${tier.popular ? 'popular' : ''}`}
                  data-wow-duration="600ms"
                  data-wow-delay={`${index * 100}ms`}
                  style={{ backgroundImage: `url(${bgItemImg})` }}
                >
                  <div className="xb-icon">
                    <img src={index % 2 === 0 ? icon1 : icon2} alt="icon" />
                  </div>

                  {/* Ship Name */}
                  <span className="d-block mb-5 opacity-75">{tier.shipName}</span>

                  {/* Discount Badge */}
                  {tier.discountPercent > 0 && (
                    <span className="premium-plan d-inline-block mb-10">
                      {t('pricing.introOffer')} - {tier.discountPercent}% OFF
                    </span>
                  )}

                  {/* Price Display with Original (crossed out) */}
                  {getOriginalPrice(tier) && (
                    <p className="mb-5">
                      <span className="text-decoration-line-through opacity-50">
                        {getOriginalPrice(tier)}
                      </span>
                    </p>
                  )}
                  <h2 className="xb-dollar">
                    {getPrice(tier)}
                    <sub>{getPeriod(tier)}</sub>
                  </h2>

                  {/* 10-Year Option for Titan */}
                  {(tier.tenYearPrice && !tier.monthlyPrice) && (
                    <div className="mb-10">
                      {tier.originalTenYearPrice && (
                        <p className="mb-0">
                          <span className="text-decoration-line-through opacity-50" style={{ fontSize: '14px' }}>
                            {formatPrice(tier.originalTenYearPrice)}
                          </span>
                        </p>
                      )}
                      <p className="mb-0">
                        <span className="fw-bold" style={{ fontSize: '20px', color: 'var(--color-primary)' }}>
                          {formatPrice(tier.tenYearPrice)}
                        </span>
                        <span className="opacity-75"> / 10 Years</span>
                      </p>
                    </div>
                  )}

                  <p className="mb-20 opacity-75">{tier.description}</p>

                  {/* CTA Button */}
                  <div className="pricing-btn mb-25">
                    {isTierLocked(tier) ? (
                      <span className="thm-btn chatbot-btn disabled" style={{ opacity: 0.6, cursor: 'not-allowed', pointerEvents: 'none' }}>
                        <span className="text">{tier.ctaText}</span>
                        <span className="btn-bg">
                          <svg width="800" height="60" viewBox="0 0 800 60" fill="none" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
                            <rect width="800" height="60" fill="url(#paint_radial_disabled)" />
                            <defs>
                              <radialGradient id="paint_radial_disabled" cx="0" cy="0" r="1" gradientTransform="matrix(-667.5 -25 0.582116 -49.7476 497 39)" gradientUnits="userSpaceOnUse">
                                <stop offset="0" stopColor="#666" />
                                <stop offset="1" stopColor="#0a0612" stopOpacity="0" />
                              </radialGradient>
                            </defs>
                          </svg>
                        </span>
                      </span>
                    ) : (
                      <Link
                        className="thm-btn chatbot-btn"
                        to={tier.ctaLink.includes('/signup')
                          ? `${tier.ctaLink}${tier.ctaLink.includes('?') ? '&' : '?'}${verticalSlug ? `vertical=${encodeURIComponent(verticalSlug)}&` : ''}billing=${billingPeriod}`
                          : tier.ctaLink}
                      >
                        <span className="text">{tier.ctaText}</span>
                        <span className="btn-bg">
                          <svg width="800" height="60" viewBox="0 0 800 60" fill="none" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
                            <rect width="800" height="60" fill="url(#paint_radial)" />
                            <defs>
                              <radialGradient id="paint_radial" cx="0" cy="0" r="1" gradientTransform="matrix(-667.5 -25 0.582116 -49.7476 497 39)" gradientUnits="userSpaceOnUse">
                                <stop offset="0" stopColor="var(--color-primary)" />
                                <stop offset="1" stopColor="#0a0612" stopOpacity="0" />
                              </radialGradient>
                            </defs>
                          </svg>
                        </span>
                      </Link>
                    )}
                  </div>

                  {/* Features List */}
                  <ul className="pricing-list list-unstyled">
                    {resolveTierFeatures(tier, verticalSlug, activeLiveVerticalLimits).slice(0, 8).map((feature, i) => (
                      <li key={i} className={feature.included ? '' : 'opacity-50'}>
                        <span>
                          {feature.included ? <CheckIcon /> : <CrossIcon />}
                        </span>
                        <span className={feature.highlight ? 'fw-bold' : ''}>
                          {feature.text}
                        </span>
                      </li>
                    ))}
                  </ul>

                  {/* Tag */}
                  <span className={`xb-tag ${tier.popular ? 'premium-plan' : 'xb-border'}`}>
                    {tier.name}
                  </span>
                </div>
              </div>
            ))}
            </div>
          )}
        </div>
      </section>

      {/* ========== FEATURE COMPARISON SECTION (moved above add-ons: it extends the tier
           decision; add-on packs are a post-decision upsell) ========== */}
      {!hideComparison && (
      <section
        className={`comparison pt-100 pb-100 ${hideBg ? "" : "bg_img"}`}
        style={hideBg ? undefined : { backgroundImage: `url(${bgImg})` }}
      >
        <div className="container">
          <div className="sec-title text-center mb-50">
            <span className="sub-title wow fadeInUp">{t('pricing.compareFeatures') || 'Compare all features'}</span>
            <h2 className="title wow fadeInUp">{t('pricing.compareSubtitle') || 'Find the perfect plan for your needs'}</h2>
          </div>

          {/* Feature Comparison Table */}
          <div className="pricing-comparison-card wow fadeInUp" data-wow-duration="600ms">
            <div className="table-responsive">
              <table className="pricing-comparison-table">
                <thead>
                  <tr>
                    <th className="feature-name-col">{comparisonHeaders.feature}</th>
                    <th>{comparisonHeaders.explorer}</th>
                    <th>{comparisonHeaders.officer}</th>
                    <th className="table-highlight">{comparisonHeaders.commander}</th>
                    <th>{comparisonHeaders.titan || 'Titan'}</th>
                  </tr>
                </thead>
                <tbody>
                  {FEATURE_COMPARISON.map((category, catIndex) => (
                    <React.Fragment key={catIndex}>
                      {/* Category Header */}
                      <tr className="category-header">
                        <td colSpan={5} className="fw-bold">{category.name}</td>
                      </tr>
                      {/* Features */}
                      {category.features.map((feature, featIndex) => (
                        <tr key={featIndex}>
                          <td className="feature-name-cell">{feature.name}</td>
                          <td>{renderComparisonValue(feature.explorer)}</td>
                          <td>{renderComparisonValue(feature.officer)}</td>
                          <td className="table-highlight">{renderComparisonValue(feature.commander)}</td>
                          <td>{renderComparisonValue(feature.titan)}</td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>

            {/* One qualification of "white-label", once — BR-045/BR-047. It is
                true on every tier, so it does not need repeating on each card.
                Wording and the Titan exemption come from Terms 6a; link there
                rather than restating the rule in two places. */}
            <div className="pricing-comparison-footer">
              <p>
                {t('pricing.whiteLabelNote')}{' '}
                <a href="/terms#section-6a" style={{ textDecoration: 'underline', color: 'var(--color-primary)' }}>
                  {t('pricing.whiteLabelNoteLink')}
                </a>
              </p>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* ========== SPECIALTY ADDONS SECTION ========== */}
      {!hideAddons && (
      <section
        className={`addons pt-100 pb-100 ${hideBg ? "" : "bg_img"}`}
        style={hideBg ? undefined : { backgroundImage: `url(${bgImg})` }}
      >
        <div className="container">
          <div className="sec-title sec-title--two text-center mb-50">
            <span className="sub-title wow fadeInUp" data-wow-duration="600ms">
              <img src={iconLeft} alt="icon" /> {t('pricing.addonsTitle') || 'Specialty Addons'} <img src={iconRight} alt="icon" />
            </span>
            <h2 className="title wow fadeInUp" data-wow-delay="150ms" data-wow-duration="600ms">
              {t('pricing.addonsSubtitle') || 'Enhance your AXOIX experience'}
            </h2>
            <p className="mt-20 opacity-75 wow fadeInUp" data-wow-delay="200ms">
              {t('pricing.addonsDescription') || 'Optional paid add-on packs for extra capacity and specialised tools. Each pack is an upsell with its own price and limits — add only what you need.'}
            </p>
          </div>

          <div className="row justify-content-center">
            {pricingLoading ? (
              <div className="col-12 text-center py-5">
                <div style={{ color: "rgba(0, 255, 151, 0.8)", fontSize: "14px", letterSpacing: "0.05em" }}>
                  <i className="fas fa-circle-notch fa-spin me-2"></i>
                  Loading Addon Packs from Mothership...
                </div>
              </div>
            ) : (
              SPECIALTY_ADDONS.map((addon, index) => (
              <div key={addon.id} className="col-lg-6 col-md-8 mt-30">
                <div
                  className="pricing-item xb-border bg_img wow fadeInUp"
                  data-wow-duration="600ms"
                  data-wow-delay={`${index * 100}ms`}
                  style={{ backgroundImage: `url(${bgItemImg})` }}
                >
                  {/* Icon */}
                  <div className="xb-icon">
                    <img src={icon1} alt={addon.name} />
                  </div>

                  {/* Badge */}
                  {addon.badge && (
                    <span
                      className="premium-plan d-inline-block mb-15"
                      style={addon.comingSoon ? { background: 'linear-gradient(135deg, #666 0%, #444 100%)' } : {}}
                    >
                      {addon.badge}
                    </span>
                  )}

                  {/* Addon Name */}
                  <h3 className="mb-10">{addon.name}</h3>
                  <p className="mb-20 opacity-75">{addon.description}</p>

                  {/* Pricing Display — one-time / validity packs have no yearly price, so we
                      show just the pack price (the badge conveys the term). Only genuinely
                      recurring add-ons (yearlyPrice > 0) show the monthly/yearly split. */}
                  <div className="d-flex align-items-center gap-4 mb-25">
                    <div className="text-center">
                      <h2 className="xb-dollar mb-0">
                        {formatPrice(addon.monthlyPrice)}
                        {addon.yearlyPrice > 0 && <sub>/ {t('pricing.monthly')}</sub>}
                      </h2>
                    </div>
                    {addon.yearlyPrice > 0 && (
                      <>
                        <span className="opacity-50">{t('pricing.or')}</span>
                        <div className="text-center">
                          <h2 className="xb-dollar mb-0">
                            {formatPrice(addon.yearlyPrice)}
                            <sub>/ {t('pricing.yearly')}</sub>
                          </h2>
                          <span className="premium-plan">{t('pricing.saveAddonPercent')}</span>
                        </div>
                      </>
                    )}
                  </div>

                  {/* CTA Button */}
                  <div className="pricing-btn mb-25">
                    {addon.comingSoon ? (
                      <span className="thm-btn chatbot-btn disabled" style={{ opacity: 0.6, cursor: 'not-allowed', pointerEvents: 'none' }}>
                        <span className="text">{t('pricing.comingSoon') || 'Coming Soon'}</span>
                        <span className="btn-bg">
                          <svg width="800" height="60" viewBox="0 0 800 60" fill="none" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
                            <rect width="800" height="60" fill="url(#paint_radial_addon_disabled)" />
                            <defs>
                              <radialGradient id="paint_radial_addon_disabled" cx="0" cy="0" r="1" gradientTransform="matrix(-667.5 -25 0.582116 -49.7476 497 39)" gradientUnits="userSpaceOnUse">
                                <stop offset="0" stopColor="#666" />
                                <stop offset="1" stopColor="#0a0612" stopOpacity="0" />
                              </radialGradient>
                            </defs>
                          </svg>
                        </span>
                      </span>
                    ) : (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          padding: "10px 16px",
                          borderRadius: 8,
                          background: "rgba(0, 255, 151, 0.06)",
                          border: "1px dashed rgba(0, 255, 151, 0.25)",
                          fontSize: 13,
                          color: "rgba(255, 255, 255, 0.78)",
                          lineHeight: 1.4,
                        }}
                        aria-label="Pack is purchasable from inside your tenant dashboard after signup"
                      >
                        <i className="fas fa-info-circle" style={{ color: "var(--color-primary)", fontSize: 14 }}></i>
                        <span>
                          Purchasable from your <strong>dashboard</strong> after signup
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Features List */}
                  <ul className="pricing-list list-unstyled">
                    {addon.features.map((feature, i) => (
                      <li key={i}>
                        <span><CheckIcon /></span>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>

                </div>
              </div>
            ))
          )}
          </div>
        </div>
      </section>
      )}

      {/* ========== FAQ HELP NOTE ========== */}
      {!hideFaqHelp && (
      <section
        id="faq"
        className={`faq-help pos-rel pt-100 pb-100 ${hideBg ? "" : "bg_img"}`}
        style={hideBg ? undefined : { backgroundImage: `url(${bgImg})` }}
      >
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-8">
              <div className="faq-help-box text-center wow fadeInUp" data-wow-duration="600ms">
                <h3 className="mb-3" style={{ color: '#fff', fontSize: '28px' }}>
                  {t('pricing.faqSubtitle') || 'Have questions about our pricing?'}
                </h3>
                <p className="mb-4" style={{ color: 'rgba(255,255,255,0.7)', fontSize: '16px' }}>
                  {t('faq.intro')}
                </p>
                <div className="d-flex justify-content-center gap-3 flex-wrap">
                  <Link to="/faq" className="thm-btn agency-btn">
                    <span className="text">{t('ui.viewAllFaqs')}</span>
                    <span className="arrow">
                      <span className="arrow-icon">
                        <svg width="20" height="19" viewBox="0 0 20 19" fill="none"><rect x="3.783" y="13.463" width="14.31" height="1.817" transform="rotate(-40.2798 3.783 13.463)" fill="white"/><rect x="5.807" y="4.605" width="1.817" height="1.817" transform="rotate(-40.2798 5.807 4.605)" fill="white"/><rect x="8.367" y="4.816" width="1.817" height="1.817" transform="rotate(-40.2798 8.367 4.816)" fill="white"/><rect x="10.926" y="5.028" width="1.817" height="1.817" transform="rotate(-40.2798 10.926 5.028)" fill="white"/><rect x="13.277" y="7.8" width="1.817" height="1.817" transform="rotate(-40.2798 13.277 7.8)" fill="white"/><rect x="13.066" y="10.361" width="1.817" height="1.817" transform="rotate(-40.2798 13.066 10.361)" fill="white"/><rect x="12.855" y="12.922" width="1.817" height="1.817" transform="rotate(-40.2798 12.855 12.922)" fill="white"/></svg>
                        <svg width="20" height="19" viewBox="0 0 20 19" fill="none"><rect x="3.783" y="13.463" width="14.31" height="1.817" transform="rotate(-40.2798 3.783 13.463)" fill="white"/><rect x="5.807" y="4.605" width="1.817" height="1.817" transform="rotate(-40.2798 5.807 4.605)" fill="white"/><rect x="8.367" y="4.816" width="1.817" height="1.817" transform="rotate(-40.2798 8.367 4.816)" fill="white"/><rect x="10.926" y="5.028" width="1.817" height="1.817" transform="rotate(-40.2798 10.926 5.028)" fill="white"/><rect x="13.277" y="7.8" width="1.817" height="1.817" transform="rotate(-40.2798 13.277 7.8)" fill="white"/><rect x="13.066" y="10.361" width="1.817" height="1.817" transform="rotate(-40.2798 13.066 10.361)" fill="white"/><rect x="12.855" y="12.922" width="1.817" height="1.817" transform="rotate(-40.2798 12.855 12.922)" fill="white"/></svg>
                      </span>
                    </span>
                  </Link>
                  <Link to="/contact" className="thm-btn agency-btn">
                    <span className="text">{t('nav.contact') || 'Contact Us'}</span>
                    <span className="arrow">
                      <span className="arrow-icon">
                        <svg width="20" height="19" viewBox="0 0 20 19" fill="none"><rect x="3.783" y="13.463" width="14.31" height="1.817" transform="rotate(-40.2798 3.783 13.463)" fill="white"/><rect x="5.807" y="4.605" width="1.817" height="1.817" transform="rotate(-40.2798 5.807 4.605)" fill="white"/><rect x="8.367" y="4.816" width="1.817" height="1.817" transform="rotate(-40.2798 8.367 4.816)" fill="white"/><rect x="10.926" y="5.028" width="1.817" height="1.817" transform="rotate(-40.2798 10.926 5.028)" fill="white"/><rect x="13.277" y="7.8" width="1.817" height="1.817" transform="rotate(-40.2798 13.277 7.8)" fill="white"/><rect x="13.066" y="10.361" width="1.817" height="1.817" transform="rotate(-40.2798 13.066 10.361)" fill="white"/><rect x="12.855" y="12.922" width="1.817" height="1.817" transform="rotate(-40.2798 12.855 12.922)" fill="white"/></svg>
                        <svg width="20" height="19" viewBox="0 0 20 19" fill="none"><rect x="3.783" y="13.463" width="14.31" height="1.817" transform="rotate(-40.2798 3.783 13.463)" fill="white"/><rect x="5.807" y="4.605" width="1.817" height="1.817" transform="rotate(-40.2798 5.807 4.605)" fill="white"/><rect x="8.367" y="4.816" width="1.817" height="1.817" transform="rotate(-40.2798 8.367 4.816)" fill="white"/><rect x="10.926" y="5.028" width="1.817" height="1.817" transform="rotate(-40.2798 10.926 5.028)" fill="white"/><rect x="13.277" y="7.8" width="1.817" height="1.817" transform="rotate(-40.2798 13.277 7.8)" fill="white"/><rect x="13.066" y="10.361" width="1.817" height="1.817" transform="rotate(-40.2798 13.066 10.361)" fill="white"/><rect x="12.855" y="12.922" width="1.817" height="1.817" transform="rotate(-40.2798 12.855 12.922)" fill="white"/></svg>
                      </span>
                    </span>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
        <span className="faq-linear-shape"></span>
      </section>
      )}
    </>
  );
};

export default PricingSection;
